import React from "react";

// Live "stack in motion" showcase using the actual tools from Vishwajeet's resume
// (Azure, Synapse, ADF, Databricks, Snowflake, Microsoft Fabric, OneLake, PySpark, Delta Lake, dbt, Power BI)
// with the real achievements from his engagements.

const tools = [
  { name: "Azure Data Factory", short: "ADF", color: "#0078D4", role: "Orchestration" },
  { name: "PySpark", short: "PySpark", color: "#E25A1C", role: "Transform" },
  { name: "dbt", short: "dbt", color: "#FF694A", role: "SQL Transform" },
  { name: "Microsoft Fabric", short: "Fabric", color: "#1E56E3", role: "Lakehouse SaaS" },
  { name: "OneLake / Delta", short: "OneLake", color: "#3B82F6", role: "Storage" },
  { name: "Databricks", short: "Databricks", color: "#EF4444", role: "Compute + ML" },
  { name: "Snowflake", short: "Snowflake", color: "#38BDF8", role: "Warehouse" },
  { name: "Azure Synapse", short: "Synapse", color: "#7C3AED", role: "Warehouse" },
  { name: "Power BI", short: "Power BI", color: "#F59E0B", role: "Analytics" },
];

const achievements = [
  { metric: "82%", label: "Faster query execution on Microsoft Fabric OneLake", color: "#1E56E3" },
  { metric: "97%", label: "Reduction in data scanned (Fabric)", color: "#3B82F6" },
  { metric: "83%", label: "Lower compute consumption on Fabric Warehouse", color: "#1E56E3" },
  { metric: "35%", label: "Snowflake credit reduction via query & cluster tuning", color: "#38BDF8" },
  { metric: "70%", label: "Databricks Delta Lake runtime reduction (20→4 min)", color: "#EF4444" },
  { metric: "10+ PB", label: "Enterprise data governed with 100% audit compliance", color: "#22C55E" },
  { metric: "20+", label: "Multi-cloud sources unified into OneLake", color: "#3B82F6" },
  { metric: "9+", label: "Years engineering enterprise data platforms", color: "#0B1B3B" },
];

const StageBox = ({ x, y, w = 200, h = 92, label, items, accent }) => (
  <g transform={`translate(${x} ${y})`}>
    <rect x="0" y="0" width={w} height={h} rx="14" fill="#ffffff" stroke="#E4EAF5" strokeWidth="1.5" />
    <rect x="0" y="0" width="6" height={h} rx="3" fill={accent} />
    <text x={w / 2} y="22" textAnchor="middle" fontSize="10" fontWeight="800" fill={accent} letterSpacing="2">{label}</text>
    {items.map((t, i) => (
      <text key={t} x={w / 2} y={44 + i * 16} textAnchor="middle" fontSize="12" fontWeight="600" fill="#0B1B3B">{t}</text>
    ))}
  </g>
);

const Packet = ({ pathId, dur = 3, delay = 0, color = "#1E56E3" }) => (
  <circle r="5" fill={color}>
    <animateMotion dur={`${dur}s`} repeatCount="indefinite" begin={`${delay}s`}>
      <mpath href={`#${pathId}`} />
    </animateMotion>
    <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur={`${dur}s`} repeatCount="indefinite" begin={`${delay}s`} />
  </circle>
);

const DataFlowShowcase = () => {
  return (
    <section className="py-24 bg-white relative overflow-hidden">
      <div className="container-x">
        <div className="max-w-3xl reveal">
          <span className="tag-chip">The V4 Stack</span>
          <h2 className="section-title mt-4">Real tools. Real outcomes. In motion.</h2>
          <p className="section-sub mt-5">
            An animated view of the platforms V4 architects and optimizes every day — with the measurable outcomes we’ve shipped across Microsoft Fabric, Databricks, Snowflake and Synapse.
          </p>
        </div>

        {/* Animated stack diagram */}
        <div className="mt-12 rounded-2xl border border-slate-200 bg-gradient-to-br from-[#F8FAFF] to-white p-6 md:p-10 overflow-hidden">
          <svg viewBox="0 0 960 340" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <path id="f1" d="M 120 90  C 260 90, 340 170, 480 170" fill="none" />
              <path id="f2" d="M 120 170 C 260 170, 340 170, 480 170" fill="none" />
              <path id="f3" d="M 120 250 C 260 250, 340 170, 480 170" fill="none" />
              <path id="f4" d="M 480 170 C 640 170, 720 90,  850 90"  fill="none" />
              <path id="f5" d="M 480 170 C 640 170, 720 170, 850 170" fill="none" />
              <path id="f6" d="M 480 170 C 640 170, 720 250, 850 250" fill="none" />
              <linearGradient id="grad1" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" stopColor="#7CA6FF" />
                <stop offset="1" stopColor="#1E56E3" />
              </linearGradient>
            </defs>

            {/* Column headers */}
            <text x="170" y="32" textAnchor="middle" fontSize="11" fontWeight="700" fill="#1E56E3" letterSpacing="2">INGEST · TRANSFORM</text>
            <text x="480" y="32" textAnchor="middle" fontSize="11" fontWeight="700" fill="#1E56E3" letterSpacing="2">LAKEHOUSE</text>
            <text x="830" y="32" textAnchor="middle" fontSize="11" fontWeight="700" fill="#1E56E3" letterSpacing="2">SERVE · CONSUME</text>

            {/* Static curve strokes */}
            {["f1","f2","f3","f4","f5","f6"].map((id) => (
              <use key={id} href={`#${id}`} stroke="#DDE5F5" strokeWidth="2" fill="none" />
            ))}
            {["f1","f2","f3","f4","f5","f6"].map((id, i) => (
              <use key={`a-${id}`} href={`#${id}`} className="flow-line" stroke="url(#grad1)" strokeWidth="2" fill="none" style={{ animationDelay: `${i * 0.15}s` }} />
            ))}

            {/* Moving packets */}
            <Packet pathId="f1" dur={3.4} delay={0.0} color="#0078D4" />
            <Packet pathId="f2" dur={3.0} delay={0.7} color="#E25A1C" />
            <Packet pathId="f3" dur={3.8} delay={1.3} color="#FF694A" />
            <Packet pathId="f4" dur={3.2} delay={0.4} color="#EF4444" />
            <Packet pathId="f5" dur={3.6} delay={1.0} color="#38BDF8" />
            <Packet pathId="f6" dur={3.4} delay={1.6} color="#F59E0B" />

            {/* Left column: Ingest & Transform tools (ADF, PySpark, dbt) */}
            <StageBox x={20}  y={54}  label="ORCHESTRATE"     items={["Azure Data Factory"]}   accent="#0078D4" />
            <StageBox x={20}  y={134} label="TRANSFORM"       items={["PySpark"]}              accent="#E25A1C" />
            <StageBox x={20}  y={214} label="SQL TRANSFORM"   items={["dbt"]}                  accent="#FF694A" />

            {/* Central Lakehouse (Fabric OneLake + Delta) */}
            <g transform="translate(480 170)">
              <circle r="74" fill="#0B1B3B" />
              <circle r="74" fill="none" stroke="#1E56E3" strokeWidth="2" opacity="0.6" className="pulse-dot" />
              <text x="0" y="-24" textAnchor="middle" fontSize="10" fontWeight="800" fill="#7CA6FF" letterSpacing="2">LAKEHOUSE</text>
              <text x="0" y="-4"  textAnchor="middle" fontSize="14" fontWeight="800" fill="#ffffff">Microsoft Fabric</text>
              <text x="0" y="14"  textAnchor="middle" fontSize="12" fontWeight="700" fill="#B8C4E0">OneLake · Delta</text>
              <text x="0" y="32"  textAnchor="middle" fontSize="10" fill="#7CA6FF">Unity Catalog · Purview</text>
            </g>

            {/* Right column: Compute + Warehouse + BI */}
            <StageBox x={740} y={54}  label="COMPUTE + ML"    items={["Databricks", "Delta Lake"]} accent="#EF4444" />
            <StageBox x={740} y={134} label="WAREHOUSE"       items={["Snowflake", "Synapse"]}    accent="#38BDF8" />
            <StageBox x={740} y={214} label="ANALYTICS + AI"  items={["Power BI"]}                accent="#F59E0B" />
          </svg>

          {/* Tool chips - all resume tools */}
          <div className="mt-8 flex flex-wrap gap-2 justify-center">
            {tools.map((t) => (
              <div
                key={t.name}
                className="group inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white hover:border-[#1E56E3] hover:bg-[#EEF3FF] transition-colors"
              >
                <span className="h-2 w-2 rounded-full pulse-dot" style={{ background: t.color }} />
                <span className="text-xs font-bold text-[#0B1B3B]">{t.short}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-500">{t.role}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Achievement grid */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {achievements.map((a) => (
            <div key={a.label} className="reveal rounded-2xl border border-slate-200 bg-white p-6 hover-lift">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full pulse-dot" style={{ background: a.color }} />
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Achievement</span>
              </div>
              <div className="mt-3 text-4xl font-extrabold text-[#0B1B3B]">{a.metric}</div>
              <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{a.label}</p>
              <div className="mt-4 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full shimmer-bar" style={{ width: "88%" }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DataFlowShowcase;
